/*
 * Cognito hosted-UI OAuth2/OIDC client (authorization-code + PKCE) for the
 * consumer pool. Pure functions over fetch — no SDK dependency. All endpoint
 * values derive from ConsumerAuthConfig (env-sourced; nothing invented).
 */
import type { ConsumerAuthConfig } from './authConfig'

export interface TokenSet {
  idToken: string
  accessToken: string
  refreshToken?: string
  expiresIn: number
}

export interface IdClaims {
  sub: string
  email?: string
  'cognito:username'?: string
  [k: string]: unknown
}

export function hostedUiBase(config: ConsumerAuthConfig): string {
  if (!config.hostedUiDomain) throw new Error('hosted UI domain not configured')
  return `https://${config.hostedUiDomain}`
}

/**
 * @deprecated Hosted-UI authorize URL. Superseded by the branded email-first
 * login (src/screens/Login.tsx + cognitoAuth.ts), which never navigates to
 * *.amazoncognito.com. Retained only so the /auth/callback route can drain
 * in-flight redirects for one deploy cycle; do not use for new sign-ins.
 */
export function buildAuthorizeUrl(
  config: ConsumerAuthConfig,
  opts: { redirectUri: string; state: string; challenge: string; signup?: boolean },
): string {
  const base = hostedUiBase(config)
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: opts.redirectUri,
    // aws.cognito.signin.user.admin is required on the ACCESS token for the
    // Profile screen's direct Cognito GetUser / UpdateUserAttributes calls
    // (src/auth/cognitoProfile.ts); without it those return 400
    // NotAuthorizedException 'Access Token does not have required scopes'.
    // Paired with infra adding it to the stage app-client's AllowedOAuthScopes;
    // a fresh sign-in mints a token carrying the new scope.
    scope: 'openid email aws.cognito.signin.user.admin',
    state: opts.state,
    code_challenge_method: 'S256',
    code_challenge: opts.challenge,
  })
  const path = opts.signup ? '/signup' : '/oauth2/authorize'
  return `${base}${path}?${params.toString()}`
}

/**
 * @deprecated Hosted-UI logout URL. Superseded by the in-app sign-out
 * (clearSession + best-effort cognitoAuth.globalSignOut) — no amazoncognito.com
 * hop on logout. Retained for the callback-drain cycle only.
 */
export function buildLogoutUrl(config: ConsumerAuthConfig, logoutUri: string): string {
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: logoutUri,
  })
  return `${hostedUiBase(config)}/logout?${params.toString()}`
}

export async function exchangeCode(
  config: ConsumerAuthConfig,
  opts: { code: string; verifier: string; redirectUri: string },
  fetchImpl: typeof fetch = fetch,
): Promise<TokenSet> {
  const res = await fetchImpl(`${hostedUiBase(config)}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.userPoolClientId,
      code: opts.code,
      redirect_uri: opts.redirectUri,
      code_verifier: opts.verifier,
    }).toString(),
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`token exchange failed (${res.status}): ${detail.slice(0, 200)}`)
  }
  const json = (await res.json()) as {
    id_token: string
    access_token: string
    refresh_token?: string
    expires_in: number
  }
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresIn: json.expires_in,
  }
}

/** Decode a JWT payload WITHOUT signature verification — acceptable only for
 * tokens received first-hand from the token endpoint over TLS. */
export function decodeJwtPayload(jwt: string): IdClaims {
  const parts = jwt.split('.')
  if (parts.length !== 3) throw new Error('not a JWT')
  const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  return JSON.parse(atob(padded)) as IdClaims
}
